import { Component } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '@services/alert.service';
import { ApiService } from '@services/api.service';
import { FormatCurrencyService } from '@services/format-currency.service';
import { ParamsService } from '@services/params.service';
import { debounceTime, tap } from 'rxjs';
import { MatDialogCustomComponent } from '../mat-dialog-custom/mat-dialog-custom.component';
import { MatDialogRef } from '@angular/material/dialog';
import { TreoMessage } from '@treo/components/message';

type ConvertRateQuoteRequest = {
  success: true;
  results: {
    currencyFrom: string;
    amountFrom: string;
    currencyTo: string;
    amountTo: string;
    rate: string;
    reverseRate: string;
    estimatedRateExpiry: string;
  };
};

@Component({
  selector: 'ew-convert-funds',
  templateUrl: './convert-funds.component.html',
  styleUrls: ['./convert-funds.component.scss'],
})
export class ConvertFundsComponent {
  loadingRateQuote: boolean = false;
  loadingAccounts: boolean = true;
  senders: any[] = [];
  selected: any;
  sender: any;
  receiver: any;
  receivers: any[] = [];
  reference = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[a-zA-Z0-9 ]+$/),
  ]);
  senderAmount = new FormControl(0, [Validators.required]);
  receiverAmount = new FormControl(0, [Validators.required]);
  fxRates: {
    baseCurrency: string;
    destCurrency: string;
  } = {
    baseCurrency: '',
    destCurrency: '',
  };
  message: TreoMessage | null = null;
  confirmation = new FormControl(false, [Validators.required]);
  unlockSendButton: boolean = true;

  constructor(
    private API: ApiService,
    public PRMS: ParamsService,
    public RT: Router,
    public ALERT: AlertService,
    public MODAL: MatDialogRef<MatDialogCustomComponent>,
    private formatCurrency: FormatCurrencyService,
  ) {
    this.getCurrencyAccounts();
    let currencyId = this.PRMS.params.get('currencyId');
    let currencyIso = this.PRMS.params.get('currencyIso');

    if (currencyId && currencyIso) {
      this.selected = {
        companyCurrencyAccountId: currencyId,
        currencyIso: currencyIso,
      };
    }

    this.senderAmount.valueChanges
      .pipe(
        tap((amount) => {
          if (amount! > 0) {
            this.loadingRateQuote = true;
          }
        }),
      )
      .pipe(debounceTime(1500))
      .subscribe((amount) => {
        this.fxRates.baseCurrency = '';
        this.fxRates.destCurrency = '';
        this.receiverAmount.setValue(0);
        if (amount && amount > 0 && amount <= this.sender.balance) {
          this.API.get<ConvertRateQuoteRequest>(
            `v1/company-currency-accounts/${this.sender.companyCurrencyAccountId}/convert/${this.receiver.companyCurrencyAccountId}/quote/${amount}`,
          ).subscribe(
            (data) => {
              const { results } = data;

              this.receiverAmount.setValue(+results.amountTo);
              const FxRate = +results.amountTo / +results.amountFrom;
              this.fxRates.baseCurrency = this.formatCurrency.formatAmount(
                1,
                results.currencyFrom.toUpperCase(),
              );
              this.fxRates.destCurrency = this.formatCurrency.formatAmount(
                +FxRate.toFixed(2),
                results.currencyTo.toUpperCase(),
              );
              this.loadingRateQuote = false;
            },
            (err) => {
              this.loadingRateQuote = false;
              this.message = {
                appearance: 'outline',
                type: 'warn',
                showButtonClose: false,
                showIcon: true,
                customIcon: '',
                title: 'alert:errorTitle',
                content: err.error?.message || 'An unexpected error occurred.',
              };
            },
          );
        } else {
          this.loadingRateQuote = false;
        }
      });
  }

  get validations() {
    return [
      this.sender !== undefined,
      this.receiver !== undefined && this.receiver !== this.sender,
      this.validateLastStep(),
    ];
  }

  get filteredReceivers() {
    return this.receivers.filter((receiver) => receiver !== this.sender);
  }

  getCurrencySymbol(currency: string): string {
    return this.formatCurrency.getCurrencySymbol(currency);
  }

  resetReceiverValues() {
    this.receiver = undefined;
    this.senderAmount.setValue(0);
    this.receiverAmount.setValue(0);
  }

  validateLastStep() {
    const regex = new RegExp(/^[a-zA-Z0-9 ]+$/);

    if (
      !!this.senderAmount.value &&
      this.senderAmount.value > 0 &&
      this.senderAmount.value <= this.sender.balance &&
      this.confirmation.value &&
      this.unlockSendButton
    ) {
      return regex.test(this.reference.value || '');
    }

    return false;
  }

  insufficientFunds() {
    return !!this.senderAmount.value
      ? this.senderAmount.value > this.sender.balance
      : false;
  }

  getCurrencyAccounts(): void {
    this.loadingAccounts = true;

    this.API.get('v1/company-currency-accounts/').subscribe(
      (data: any) => {
        this.senders = data.records.filter(
          (receiver: any) => receiver.balance > 0,
        );
        this.receivers = data.records;

        this.loadingAccounts = false;
      },
      (error) => (this.loadingAccounts = false),
    );
  }

  convertCurrency() {
    this.unlockSendButton = false;
    this.API.post(
      `v1/company-currency-accounts/${this.sender.companyCurrencyAccountId}/convert/${this.receiver.companyCurrencyAccountId}/quote/${this.senderAmount.value}`,
      { reference: this.reference.value },
    ).subscribe(
      (_data: any) => {
        this.ALERT.success('createConvertFunds:successfullyConverted');

        setTimeout(() => {
          this.MODAL.close();
          this.MODAL.afterClosed().subscribe(() => {
            this.RT.navigate(['conversions', _data.results.id, 'details']);
          });
        }, 3_000);
      },
      (err) => {
        this.message = {
          appearance: 'outline',
          type: 'warn',
          showButtonClose: false,
          showIcon: true,
          customIcon: '',
          title: 'alert:errorTitle',
          content: err.error?.message || 'An unexpected error occurred.',
        };
        this.unlockSendButton = true;
      },
    );
  }
}
